import { Company, CompanyOption } from 'types/company'

/**
 * Helper function to convert Company data to CompanyOption format
 *
 * @param companies - Array of Company objects from API
 * @returns CompanyOption[] - Simplified company data for dropdowns
 */
export const convertToCompanyOptions = (
  companies: Company[]
): CompanyOption[] => {
  return companies
    .filter((company: Company) => company.id && company.name)
    .map((company: Company) => ({
      id: company.id,
      name: company.name,
    }))
}

export type CompanySelectOption = {
  value: string
  label: string
}

export type CompanySelectOptionsConfig = {
  /** Key to use for the option value. Defaults to 'name' */
  valueKey?: 'id' | 'name'
}

/**
 * Helper function to convert Company data to Ant Design Select options format
 * Returns sorted options by company name
 *
 * @param companies - Array of Company objects from API
 * @param config - Configuration options
 * @param config.valueKey - Key to use for option value ('id' or 'name'). Defaults to 'name'
 * @returns CompanySelectOption[] - Options for Ant Design Select component
 *
 * @example
 * // Use company name as value (default)
 * convertToCompanySelectOptions(companies)
 *
 * @example
 * // Use company id as value
 * convertToCompanySelectOptions(companies, { valueKey: 'id' })
 */
export const convertToCompanySelectOptions = (
  companies: Company[],
  config?: CompanySelectOptionsConfig
): CompanySelectOption[] => {
  const { valueKey = 'name' } = config || {}

  return companies
    .filter((company: Company) => company.name && company.id)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .map((company: Company) => ({
      value: company[valueKey],
      label: company.name,
    }))
}
